import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, GripVertical } from "lucide-react";
import { getPipeline, setOpportunityStage, setContactStage, PIPELINE_STAGES } from "@/lib/crm.functions";
import { SectionTabs, CRM_TABS } from "@/components/admin/SectionTabs";

const fmtUsd = (n: number) => `$${Math.round(n).toLocaleString()}`;

export const Route = createFileRoute("/_authenticated/admin/pipeline")({
  component: Pipeline,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="text-center py-20">
        <p className="text-red-400 mb-3">{error.message}</p>
        <button onClick={() => { router.invalidate(); reset(); }} className="bg-white text-zinc-950 px-5 py-2 rounded-full text-sm font-semibold">Try again</button>
      </div>
    );
  },
});

const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—");

function Pipeline() {
  const load = useServerFn(getPipeline);
  const setStage = useServerFn(setOpportunityStage);
  const setCStage = useServerFn(setContactStage);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["crm", "pipeline"], queryFn: () => load(), staleTime: 15_000 });
  const move = useMutation({
    mutationFn: (v: { id: string; stage: any; lost_reason?: string; kind?: string }) =>
      v.kind === "contact"
        ? setCStage({ data: { id: v.id, stage: v.stage, reason: v.lost_reason } })
        : setStage({ data: { id: v.id, stage: v.stage, lost_reason: v.lost_reason } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["crm"] }),
  });

  if (isLoading) return <div className="grid place-items-center py-24"><Loader2 className="size-6 animate-spin text-amber-300" /></div>;
  if (!data) return null;

  const grouped = data.grouped ?? {};
  const totals = data.totals ?? {};
  const summary = data.summary ?? { openValue: 0, weightedValue: 0, wonValue: 0, lostValue: 0, avgDealSize: 0 };
  const kindById: Record<string, string> = {};
  for (const list of Object.values(grouped) as any[][]) for (const r of list) kindById[r.id] = r.kind;

  const handleMove = (id: string, stage: string) => {
    const kind = kindById[id];
    if (stage === "lost") {
      const reason = typeof window !== "undefined" ? window.prompt("Lost reason (optional):") ?? undefined : undefined;
      move.mutate({ id, stage, lost_reason: reason || undefined, kind });
    } else {
      move.mutate({ id, stage, kind });
    }
  };

  return (
    <div className="space-y-6">
      <SectionTabs tabs={CRM_TABS} label="CRM" />
      <header>
        <p className="text-amber-300 text-[11px] tracking-[0.25em] uppercase font-semibold">CRM</p>
        <h1 className="font-serif text-3xl text-white mt-1">Pipeline</h1>
        <p className="text-sm text-zinc-400 mt-2">Drag or use the menu to move opportunities between stages.</p>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Open Pipeline", value: summary.openValue, tone: "text-amber-200" },
          { label: "Weighted", value: summary.weightedValue, tone: "text-amber-200" },
          { label: "Won Sales", value: summary.wonValue, tone: "text-emerald-300" },
          { label: "Lost Value", value: summary.lostValue, tone: "text-zinc-400" },
          { label: "Avg Deal Size", value: summary.avgDealSize, tone: "text-white" },
        ].map((k) => (
          <div key={k.label} className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500">{k.label}</p>
            <p className={`text-lg font-semibold mt-1 tabular-nums ${k.tone}`}>{fmtUsd(k.value)}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-flow-col auto-cols-[minmax(260px,1fr)] gap-3 overflow-x-auto pb-4">
        {PIPELINE_STAGES.map((stage) => {
          const rows = grouped[stage.key] ?? [];
          const stageTotal = totals[stage.key] ?? 0;
          return (
            <div
              key={stage.key}
              className="rounded-2xl border border-white/10 bg-white/[0.02] flex flex-col min-h-[400px]"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                const id = e.dataTransfer.getData("text/plain");
                if (id) handleMove(id, stage.key);
              }}
            >
              <div className="px-3 py-2.5 border-b border-white/5 flex items-center justify-between">
                <p className="text-xs uppercase tracking-widest text-zinc-400">{stage.label}</p>
                <span className="text-[11px] text-zinc-400 tabular-nums">
                  {rows.length} · <span className="text-white">{fmtUsd(stageTotal)}</span>
                </span>
              </div>
              <div className="p-2 space-y-2 flex-1">
                {rows.length === 0 && <p className="text-[11px] text-zinc-600 text-center py-8">No opportunities</p>}
                {rows.map((c: any) => (
                  <div
                    key={c.id}
                    draggable
                    onDragStart={(e) => e.dataTransfer.setData("text/plain", c.id)}
                    className="group rounded-xl border border-white/10 bg-white/[0.03] p-3 hover:border-amber-300/30 cursor-grab active:cursor-grabbing"
                  >
                    <div className="flex items-start gap-2">
                      <GripVertical className="size-3.5 text-zinc-600 mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <Link to="/admin/contacts/$id" params={{ id: c.contact_id }} className="block">
                          <p className="text-sm text-white truncate group-hover:text-amber-200">{c.name ?? c.email ?? "Unnamed"}</p>
                          <p className="text-[11px] text-zinc-500 truncate mt-0.5">{c.email ?? c.phone ?? ""}</p>
                          {c.project_title || c.service_type ? (
                            <p className="text-[11px] text-zinc-400 truncate mt-1 capitalize">{c.project_title ?? c.service_type}</p>
                          ) : null}
                          <p className="text-sm text-white tabular-nums font-semibold mt-1">{fmtUsd(Number(c.opportunity_value ?? 0))}</p>
                          <p className="text-[10px] text-zinc-600 mt-1">Last active {fmtDate(c.last_activity_at)}</p>
                        </Link>
                        <select
                          value={stage.key}
                          onChange={(e) => handleMove(c.id, e.target.value)}
                          className="mt-2 w-full text-[11px] bg-black/40 border border-white/10 rounded px-1.5 py-1 text-zinc-300 focus:outline-none focus:border-amber-300/40"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {PIPELINE_STAGES.map((s) => (
                            <option key={s.key} value={s.key}>Move to {s.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}